// ─────────────────────────────────────────────────────────────────────────────
// Application root: providers, then the shell.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, type ReactElement } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import superjson from "superjson";
import { trpc } from "../api";
import { I18nProvider } from "../i18n";
import { Shell } from "./Shell";

export function App(): ReactElement {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        // Business data changes when someone acts on it, not on a timer.
        // Refetching aggressively makes numbers flicker mid-read; mutations
        // invalidate what they touched instead.
        staleTime: 30_000,
        /**
         * Refetch on focus ONLY what is currently failing.
         *
         * This was a flat `false`, and the reasoning behind it — a figure must
         * not change under somebody's eyes while they are reading it — only
         * applies to a query that HAS a figure. A failed query has nothing to
         * protect: it is showing an error, and refetching can only replace it
         * with data or with the same error.
         *
         * Left as `false`, an error is permanent for the life of the tab.
         * `file_base64 does not exist` kept appearing for hours after the
         * migration that fixed it had been applied, because the tab had no
         * reason to ask again. The database had healed; the browser had not
         * noticed, and could not.
         */
        refetchOnWindowFocus: (query) => query.state.status === "error",
        retry: (failureCount, error): boolean => {
          const code = (error as { data?: { code?: string } }).data?.code;
          // Never retry a rejection the server made deliberately.
          if (code === "UNAUTHORIZED" || code === "FORBIDDEN" || code === "BAD_REQUEST" || code === "UNPROCESSABLE_CONTENT" || code === "CONFLICT") return false;
          return failureCount < 2;
        },
      },
    },
  }));

  const [trpcClient] = useState(() => trpc.createClient({
    links: [httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch: (url, options) => fetch(url, { ...options, credentials: "include" }),
    })],
  }));

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <I18nProvider>
          <Shell />
        </I18nProvider>
      </QueryClientProvider>
    </trpc.Provider>
  );
}
